import React, { useEffect, useState } from 'react';
import { PhoneCall } from 'lucide-react';
import { useCallStatus } from '../../../core/telephony/useCallStatus';
import { busyLine, callLine, windowLine } from '../../../core/telephony/callStatus';

const Ping = () => (
    <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600" />
    </span>
);

/**
 * "Last call: 29 Oct, 05:45 pm (18 min ago) by SREE KALA — Not Answered", shown BEFORE
 * anyone dials (owner, 4 Nov 2026: agents only saw it after the call had started, so
 * they had to cut it). Red when another agent is on this customer right now, amber when
 * another agent rang them recently. Renders nothing for staff without telecaller access.
 *
 * `compact` = one small line, for drawers and modals.
 */
const LastCallBanner = ({ mobile, compact = false, className = '' }) => {
    const { status: s, loaded } = useCallStatus(mobile);
    const [, setTick] = useState(0);
    // Keep "18 min ago" honest while the page stays open.
    useEffect(() => { const t = setInterval(() => setTick(n => n + 1), 60_000); return () => clearInterval(t); }, []);
    if (!loaded || !s) return null;

    const now = Date.now();
    const busy = !!s.busy;
    const recent = s.warn === 'recent';
    const others = windowLine(s);

    if (compact) {
        if (!busy && !s.lastCall) return null;
        return (
            <div className={`text-xs rounded-lg px-2.5 py-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 ${busy ? 'bg-red-50 text-red-800' : recent ? 'bg-amber-50 text-amber-900' : 'bg-slate-50 text-slate-600'} ${className}`}>
                {busy && <span className="flex items-center gap-1.5 font-bold"><Ping /> {busyLine(s.busy, now)}</span>}
                {s.lastCall && <span className="flex items-center gap-1"><PhoneCall size={11} className="shrink-0 opacity-60" /> Last call: {callLine(s.lastCall, now)}</span>}
                {others && <span className="font-semibold">· {others}</span>}
            </div>
        );
    }

    const tone = busy ? 'bg-red-50 border-red-200 text-red-900'
        : recent ? 'bg-amber-50 border-amber-200 text-amber-900'
            : 'bg-slate-50 border-slate-200 text-slate-700';
    return (
        <div className={`rounded-xl border px-4 py-2 text-sm flex flex-wrap items-center gap-x-4 gap-y-1 ${tone} ${className}`}>
            {busy && <span className="flex items-center gap-2 font-bold text-red-700"><Ping /> {busyLine(s.busy, now)}</span>}
            {s.lastCall ? (
                <span className="flex items-center gap-1.5">
                    <PhoneCall size={14} className="shrink-0 opacity-60" />
                    <span><b>Last call:</b> {callLine(s.lastCall, now)}</span>
                </span>
            ) : (
                <span className="flex items-center gap-1.5 text-slate-500"><PhoneCall size={14} className="shrink-0 opacity-60" /> No calls to this customer yet.</span>
            )}
            {others && <span className="text-xs font-semibold">{others}</span>}
            {s.lastCall?.remark && <span className="w-full text-xs opacity-80">“{s.lastCall.remark}”</span>}
        </div>
    );
};

export default LastCallBanner;
